<template>
  <el-container class="loophole-scan">
    <el-container class="main-container">
      <el-main class="main-content">
        <div class="content-header">
          <h2><img src="../../../assets/codeScan/code_scan_index.png" /> {{ $t('LoopholeScan.codeScan') }}</h2>
          <el-button
            type="primary"
            class="new-scan-button"
            @click="newScan"
          >{{ $t('LoopholeScan.newScan') }}</el-button>
        </div>
        <div class="filter-container">
          <img
            src="../../../assets/codeScan/notification-icon-filter.png"
            alt="Filter Icon"
            class="filter-icon"
          />
          <el-form
            :model="searchForm"
            :inline="true"
            label-width="100px"
            :show-message="false"
          >
            <el-form-item :label="$t('LoopholeScan.projectName')">
              <el-input
                size="small"
                v-model="searchForm.projectName"
                :placeholder="$t('LoopholeScan.search')"
                class="filter-input"
                clearable
              />
            </el-form-item>
            <el-form-item :label="$t('LoopholeScan.createdOn')">
              <el-date-picker
                v-model="searchForm.createdOn"
                size="small"
                type="daterange"
                :start-placeholder="$t('LoopholeScan.startDate')"
                :end-placeholder="$t('LoopholeScan.endDate')"
                value-format="MM-dd-yyyy"
                @change="handleDateChange"
              ></el-date-picker>
            </el-form-item>
            <el-form-item :label="$t('LoopholeScan.taskStatus')">
              <el-select
                v-model="searchForm.taskStatus"
                :placeholder="$t('LoopholeScan.select')"
                size="small"
                clearable
              >
                <el-option
                  v-for="item in taskStatusOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-form-item>
          </el-form>
          <el-button
            type="primary"
            size="small"
            @click="handleSearch"
          >{{ $t('LoopholeScan.search') }}</el-button>
        </div>
        <el-table
          ref="table"
          v-loading="tableLoading"
          :data="projects"
          style="width: 100%"
          @selection-change="handleSelectionChange"
        >
          <el-table-column type="expand">
            <template slot-scope="props">
              <project-info :taskId="props.row.taskId" />
            </template>
          </el-table-column>
          <el-table-column
            prop="taskId"
            :label="$t('LoopholeScan.taskId')"
          />
          <el-table-column
            prop="projectName"
            :label="$t('LoopholeScan.projectName')"
          />
          <el-table-column
            prop="createdAt"
            :label="$t('LoopholeScan.createdOn')"
          >
            <template slot-scope="scope">
              {{ dateFormat(scope.row.createdAt) }}
            </template>
          </el-table-column>
          <el-table-column
            prop="taskStatus"
            :label="$t('LoopholeScan.taskStatus')"
          >
            <template slot-scope="scope">
              <el-tag
                :type="getStatusTagType(scope.row.taskStatus)"
                size="small"
              >
                {{ getTaskStatusLabel(scope.row.taskStatus) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column
            prop="report"
            :label="$t('LoopholeScan.report')"
          >
            <template slot-scope="scope">
              <el-tag
                size="small"
                :style="scope.row.taskStatus === 'SUCCESS' ? 'cursor: pointer' : 'cursor: not-allowed'"
                :class="{'black-background': scope.row.taskStatus === 'SUCCESS', 'gray-background': scope.row.taskStatus !== 'SUCCESS'}"
                @click="handleReportClick(scope.row)"
              >
                {{ $t('LoopholeScan.view') }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column
            prop="vulnerability"
            :label="$t('LoopholeScan.vulnerability')"
          >
            <template slot-scope="scope">
              {{ scope.row.resultsStat ? scope.row.resultsStat.total : 0 }}
            </template>
          </el-table-column>
          <el-table-column :label="$t('LoopholeScan.delete')">
            <template slot-scope="scope">
              <img
                class="bookmark"
                src="@assets/codeScan/notification-icon-del.png"
                alt=""
                @click="handleDelete(scope.row)"
              />
            </template>
          </el-table-column>
        </el-table>
        <el-footer style="height:auto">
          <el-pagination
            :current-page="queryForm.pageNo"
            :layout="layout"
            :pager-count="5"
            :page-size="queryForm.pageSize"
            :total="total"
            @current-change="handleCurrentChange"
            @size-change="handleSizeChange"
          >
          </el-pagination>
        </el-footer>
      </el-main>
    </el-container>
    <el-dialog
      ref="dialog"
      v-el-drag-dialog
      width="28%"
      :visible.sync="dialogVisible"
      :modal="false"
      class="httpMain"
    >
      <div
        slot="title"
        class="dialog-title"
      >
        <span class="dialog-title-text">{{ $t('LoopholeScan.httpHeader') }}</span>
      </div>
      <div>{{ $t('LoopholeScan.cookie') }}: {{ selectedTestAccount[0].cookiie }}</div>
      <div>{{ $t('LoopholeScan.authorization') }}: {{ selectedTestAccount[0].authorization }}</div>
    </el-dialog>
    <el-dialog
      :visible.sync="deleteVisible"
      width="500px"
      :title="$t('LoopholeScan.deleteTask')"
    >
      <div style="text-align: center; font-size: 18px">{{ $t('LoopholeScan.deleteConfirmation') }} <span style="font-weight: bold;">{{deleteTaskName}}</span> ?</div>
      <span
        slot="footer"
        class="dialog-footer"
      >
        <el-button @click="deleteVisible = false">{{ $t('LoopholeScan.cancel') }}</el-button>
        <el-button
          type="primary"
          @click="confirmDelete"
        >{{ $t('LoopholeScan.confirm') }}</el-button>
      </span>
    </el-dialog>
  </el-container>
</template>

<script>
import { getTaskList, deleteTask } from '@/api/base';
import { dateLocaleFormat, utcDateFormat } from '@/lib/date-format';
import { TaskStatusOptions } from '@/enum';
import ProjectInfo from '@/components/project-info';
import { storage } from '@/utils/storage';
import { ScanDataKey, RemoteBrowserUrlKey } from '@/enum';
import { mapGetters } from 'vuex';

export default {
  name: 'LoopholeScan',
  components: {
    ProjectInfo,
  },
  data() {
    return {
      deleteVisible: false,
      taskStatusOptions: Object.freeze(TaskStatusOptions),
      searchForm: {
        projectName: '',
        createdOn: [],
        taskStatus: '',
      },
      tableLoading: false,
      filter: '',
      selectedProjects: [],
      selectedFilters: [],
      showDatePicker: false,
      selectedDate: null,
      filterIndex: null,
      dialogVisible: false,
      isFullScreen: false,
      selectedTestAccount: [
        {
          cookiie:
            'opm-userId=1960; op_prod_token=Bearer%20eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJleHAiOjE3MTc1NDg4NjIsImlhdCI6MTcxNzQ2NjA2MiwianRpIjoiNTE1IiwiZ2l2ZW5fbmFtZSI6IuWNoOWHryIsImVtcGxveWVlX2lkIjoiMTk2MDE5Iiwicm9sZV9pZHMiOiIzNyw3MSwyMyw4OSwxMDYsOTEsMTAyLDczIn0.Emm-E3jGiMjW_B6AI2G8-I-tYj8D2nrRh6xVcSy9hGc;',
          authorization: 'Bearer%20eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJleHAiOjE3MTc1NDg4NjIsImlhdCI6MTcxNzQ2NjA2MiwianRpIjoiNTE1IiwiZ2l2ZW',
        },
      ],
      layout: 'prev, pager, next, jumper',
      total: 0,
      queryForm: {
        pageNo: 1,
        pageSize: 10,
      },
      currentPage: 1,
      content:
        'Your code assessment task has been created, however, since our assessment engine received a large number of project assessment applications, this project need to wait in the project queue for a period of time, we will start launch security assessment as soon as once the resources are allocated, and you will be notified via email when the project is launched.',
      projects: [],
      selectedProject: {
        name: 'OpenCMS',
        provideCode: {
          war: 'openCMS-v1.2.3.war',
          zip: 'frontend-v-0.2.zip',
        },
        testAccount: ['Administrator | admin | credential recorded', 'Normal User    |  chris  | credential recorded', 'Normal User    |  test2  | credential recorded'],
        signedNda: 'https://nda.oxpecker.ai/2024072701',
        targetServer: 'https://172.3.44.123/opencms/',
      },
      deletedTaskList: [],
      taskDetailList: [],
    };
  },
  computed: {
    ...mapGetters('common', ['locale']),
    deleteTaskName() {
      return this.deletedTaskList.map((task) => task.projectName).join(', ');
    },
  },
  mounted() {
    this.getTaskList();
  },
  methods: {
    getTaskStatusLabel(status) {
      return TaskStatusOptions.find((item) => item.value === status).label;
    },
    handleSearch() {
      const filters = [];

      if (this.searchForm.projectName) {
        filters.push({
          field: 'projectName',
          op: 'LIKE',
          value: this.searchForm.projectName,
        });
      }

      if (this.searchForm.createdOn && this.searchForm.createdOn.length) {
        filters.push(
          {
            field: 'createdAt',
            op: 'LE',
            value: utcDateFormat(this.searchForm.createdOn[1]),
          },
          {
            field: 'createdAt',
            op: 'GT',
            value: utcDateFormat(this.searchForm.createdOn[0]),
          }
        );
      }

      if (this.searchForm.taskStatus) {
        filters.push({
          field: 'taskStatus',
          op: 'EQ',
          value: this.searchForm.taskStatus,
        });
      }

      this.getTaskList(filters);
    },
    handleReportClick(row) {
      if (row.taskStatus === 'SUCCESS') {
        this.$router.push({
          name: 'ReportsView',
          query: {
            taskId: row.taskId,
          },
        });
      }
    },
    dateFormat(date) {
      return dateLocaleFormat(date, this.locale);
    },
    getTaskList(filters = []) {
      this.tableLoading = true;
      const params = {
        page: this.queryForm.pageNo - 1,
        size: this.queryForm.pageSize,
        filters,
      };

      getTaskList(params)
        .then((res) => {
          this.projects = res.data;
          this.total = res.total;
        })
        .finally(() => {
          this.tableLoading = false;
        });
    },
    handleFilterChange(value) {
      const index = value.indexOf('createBefore');
      if (index !== -1) {
        this.filterIndex = index;
        this.selectedFilters = this.selectedFilters.filter((item) => item !== 'createBefore');
        this.showDatePicker = true;
        this.$nextTick(() => {
          this.$refs.filterSelect.blur();
          if (this.$refs.hiddenDatePicker) {
            this.$refs.hiddenDatePicker.showPicker();
          } else {
            console.error('Cannot find hiddenDatePicker ref');
          }
        });
      }
    },
    handleDateChange(date) {
      this.selectedDate = date;
      this.showDatePicker = false;

      const createBeforeIndex = this.selectedFilters.findIndex((item) => !isNaN(Date.parse(item)));
      if (createBeforeIndex !== -1) {
        this.selectedFilters.splice(createBeforeIndex, 1, this.selectedDate);
      } else {
        this.selectedFilters.push(this.selectedDate);
      }

      console.log('Selected date is: ' + this.selectedDate);
    },
    handleSizeChange(val) {
      this.queryForm.pageSize = val;
    },
    handleCurrentChange(val) {
      this.queryForm.pageNo = val;

      this.handleSearch();
    },
    handleDelete(row) {
      this.deleteVisible = true;
      this.deletedTaskList = [row];
    },
    confirmDelete() {
      this.deleteVisible = false;

      if (this.deletedTaskList.length > 0) {
        this.tableLoading = true;
        const taskIds = this.deletedTaskList.map((task) => task.taskId);

        deleteTask({ taskIds })
          .then(() => {
            this.$message.success(this.$t('LoopholeScan.deleteSuccess'));
            this.handleSearch();
          })
          .catch(() => {
            this.$message.error(this.$t('LoopholeScan.deleteFailed'));
            this.tableLoading = false;
          });
      }
    },
    handleSelectionChange(val) {
      this.selectedProjects = val;
    },
    newScan() {
      this.$router.push({ name: 'MainContent' });

      storage.remove(ScanDataKey);
      storage.remove(RemoteBrowserUrlKey);
    },
    getStatusTagType(status) {
      switch (status) {
        case 'RUNNING':
          return 'warning';
        case 'SUCCESS':
          return 'success';
        case 'CREATED':
          return 'info';
        case 'FAILED':
          return 'danger';
        default:
          return 'default';
      }
    },
    tableRowClassName({ row }) {
      if (this.selectedProjects.includes(row)) {
        return 'selected-row';
      }
      return '';
    },
    showDialog() {
      this.dialogVisible = true;
    },
    handleClose() {
      this.dialogVisible = false;
    },
  },
};
</script>

<style lang="scss">
.el-tooltip__popper {
  font-size: 14px;
  width: 14% !important;
  text-align: justify;
  text-justify: newspaper;
  word-break: break-all;
  line-height: 20px;
}

.el-picker-panel {
  top: 18% !important;
  left: 20% !important;
  border-radius: 10px !important;
}

.loophole-scan {
  width: 100%;
  display: flex;

  .header-title {
    font-size: 17px;
    display: flex;
    align-items: center;
    padding-top: 4px;

    font-weight: 400;
    color: #606266;

    > div {
      padding-top: 2px;
      margin-right: 15px;
    }
  }

  .el-form-item {
    margin-bottom: 0;
  }

  .tooltip-width {
    width: 265px;
  }

  .sidebar {
    height: calc(100vh - 85px);
    margin: 60px 0px;
    padding: 0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border-radius: 10px;
  }

  .el-menu {
    background: linear-gradient(to bottom, #e6ecf1, #fff);
    height: calc(100vh - 85px);
    border-radius: 10px;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    background-color: #fff;
    margin: 0;
    border-radius: 10px;
    width: 100%;
    position: absolute;
    top: 0px;
    right: 0px;
  }

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  .el-container {
  }

  .main-content {
    flex: 1;
    background-color: #fff;
    border-radius: 10px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .underImgView {
    width: 20px;
    height: 20px;
    cursor: pointer;
    position: relative;
    top: 5px;
    left: 10px;
  }

  .el-footer {
    display: flex;
    justify-content: right;
    margin-top: 18px;
  }

  .logo {
    width: 150px;
  }

  .user-info {
    display: flex;
    align-items: center;
  }

  .username {
    margin-right: 10px;
  }

  .user-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
  }

  .content-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    background-color: #e1e8ed;
    border-radius: 10px;
    padding: 0px 20px;
    height: 55px;
    line-height: 54px;

    h2 {
      font-size: 20px;
    }

    img {
      position: relative;
      top: 8px;
    }
  }

  .new-scan-button {
    background-color: #000;
    color: #fff;
    border: 2px solid #000;
    width: 125px;
    height: 35px;
    line-height: 5px;
  }

  .project-info-container {
    position: relative;
    top: 70%;
    border-radius: 10px;
    padding: 13px 10px;
    width: 99.4%;

    .el-card__header {
      background-color: #000;
      color: #fff;
      border-radius: 5px;
      height: 50px;
      line-height: 0;
      padding: 5px 20px;
    }

    .project-details {
      display: flex;
      justify-content: space-between;
      border-bottom: 2px solid #e1e1e1;
      line-height: 30px;

      div {
        width: 45%;
        font-size: 14px;
      }

      .details-right {
        text-align: left;
        position: relative;
        right: 360px;

        span {
          margin-left: 50px;
          font-size: 12px;
        }
      }

      span {
        margin-left: 50px;
        font-size: 12px;
      }

      .details-zip {
        span {
          margin-left: 165px;
          font-size: 12px;
        }
      }

      .details-rights {
        text-align: left;
        position: relative;
        right: 360px;

        span {
          margin-left: 20px;
          font-size: 12px;
        }
      }

      .view-img {
        width: 16px;
        margin: -5px 15px;
      }

      .details-rightss {
        text-align: left;
        position: relative;
        right: 380px;

        span {
          margin-left: 20px;
          font-size: 12px;
        }
      }
    }

    a {
      color: #333;
      text-decoration: none;
    }
  }

  .again {
    border: 2px solid #2a2a2a;
    background-color: #2a2a2a;
    color: #fff;
    font-size: 12px;
    border-radius: 5px;
    padding: 0px 15px;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  .filter-container {
    display: flex;
    align-items: center;
    margin: 20px;
  }

  .filter-icon {
    width: 20px;
    height: 20px;
    margin-right: 10px;
  }

  .filter-select {
    flex: 1;
    margin-right: 10px;
  }

  .el-table__row:hover {
    color: #333;
    border-radius: 10px;
  }

  .selected-row {
    background-color: #000 !important;
    color: #fff;
    border-radius: 10px;
  }

  .el-table th.el-table__cell {
    background-color: #eeeeee;
  }

  .bookmark {
    cursor: pointer;
    width: 30px;
    height: 30px;
  }

  .black-background {
    background-color: #000 !important;
    color: #fff !important;
    border-radius: 5px;
  }

  .gray-background {
    background-color: #ccc !important;
    color: #fff !important;
    border-radius: 5px;
  }

  .dialog-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #000;
    color: #fff;
    padding: 10px;
  }

  .dialog-title-text {
    font-size: 16px;
  }

  .dialog-fullscreen-button {
    background-color: transparent;
    border: none;
    color: #fff;
  }

  .httpMain {
    .el-dialog__header {
      padding: 0px 0px 0px;
    }

    .el-dialog__body {
      color: #333;
    }

    .el-dialog__headerbtn .el-dialog__close {
      color: #fff;
    }

    .el-dialog__headerbtn {
      top: 10px;
      color: #fff !important;
    }
  }
}
</style>
